// src/auditoria/components/ds/DsKpiCard.tsx
//
// ----------------------------------------------------------------------
// O DEFAULT AGORA É O MODELO DO ESPAÇO DE TRABALHO
//
// O dono do produto abriu `/dashboard/analytics`, aprovou os cards de KPI daquela
// tela e pediu para padronizar todos os 96 cards (27 arquivos) por eles. O modelo
// aprovado é a combinação que `espaco-trabalho-view.tsx` usava explicitamente:
//
//     <DsKpiCard ... iconTile ornament sx={{ padding: '24px 20px 24px 24px' }} />
//
// Padronizar aqui NÃO é documentar uma prop e torcer para alguém passar: a rodada
// UI-1 fez 280 mudanças como props opt-in, ninguém adotou, o pixel não mudou e o
// dono do produto abriu o app e disse que nada tinha mudado. Então o que muda é o
// DEFAULT do componente — quem não passa nada recebe o visual do modelo:
//
//   | prop       | default antes | default agora                        |
//   | ---------- | ------------- | ------------------------------------ |
//   | `ornament` | `false`       | `true`  (blob decorativo)            |
//   | `iconTile` | `false`       | `true`  (tile 40×40, gradiente)      |
//   | `density`  | `'default'`   | `'wide'` = `'24px 20px 24px 24px'`   |
//
// A porta de saída continua aberta: `ornament={false}` / `iconTile={false}` /
// `density="dense"` desligam caso a caso. Mudou o default, não sumiu a opção.
//
// CONSEQUÊNCIA PARA O CANVAS — LEIA ANTES DE "CONSERTAR"
// A spec (`docs_new/ui2/DESIGN-SYSTEM.md` §5.2) mede paddings DIFERENTES por
// família de artboard: `'20px 22px'` em dte/ordem-servico, `'18px'` nos dashboards
// de malha, `'16px 18px'` no portal e `'24px 20px 24px 24px'` em espaço de
// trabalho. Adotar um valor só para todo mundo **diverge da spec de propósito**:
// é decisão do dono do produto, não descuido nem medição errada. Quem for
// reconciliar canvas e código na próxima rodada deve atualizar a §5.2 para o valor
// único, e não o componente para os quatro valores antigos.
//
// ----------------------------------------------------------------------
//
// Implementa `.kpi-total`, `.kpi-title` e `.kpi-sub` do canvas
// (DESIGN-SYSTEM.md §5.2), mais o card KPI, o blob decorativo e o tile 40×40 de
// ícone. Instancia em **35** artboards (as classes aparecem 79/79/85 vezes).
//
// Aposenta `src/modules/dashboards/components/KpiCard.tsx`,
// `KpiCardGerencial.tsx` e o uso de `CourseWidgetSummary` como KPI.
//
// PROIBIDO: o hover `translateY(-2px)` + `boxShadow: 4` das views de dashboard
// de hoje — não existe em artboard nenhum.
//
// ----------------------------------------------------------------------
// ÍCONE E TOM VÊM DO VOCABULÁRIO, NÃO DA TELA
//
//     <DsKpiCard conceito="dinheiro-a-recuperar" title="ISS a Recuperar" ... />
//
// `conceito` resolve o par (ícone, tom) pelo mapa de `vocabulario-kpi.ts`, que é a
// mesma tabela nos dois apps. Duas telas que contam a mesma coisa leem a mesma
// linha e não têm como divergir — foi a escolha em paralelo, tela a tela, que gerou
// as 20 divergências que o conferente achou. `icon`/`tone` explícitos continuam
// vencendo o conceito, para o card cujo tom depende do dado.
//
// ----------------------------------------------------------------------
// RODADA DE CONSOLIDAÇÃO DA CAMADA COMPARTILHADA
// DECISOES-PENDENTES.md §3.7 — **6 lotes** (L01, L03, L04, L05, L06, L19).
//
// Tudo abaixo é ADITIVO: nenhuma das 75 chamadas existentes muda de aparência,
// porque todo default reproduz exatamente o comportamento anterior.
//
//  1. `loading` (L03, L04, L05) — L05 chegou a escrever um `KpiEsqueleto` local e
//     L03/L04 passaram `<Skeleton>` como `total`. O esqueleto agora nasce DENTRO
//     das mesmas caixas de texto (`<p>` com o `fontSize` da escala), então a
//     geometria é idêntica por construção e a faixa de KPIs não "pula" quando a
//     resposta chega.
//  2. `density` (L01 F3) — a escala de padding que §5.2 mede artboard a artboard:
//     `'20px 22px'` (default), `'18px'` nos 9 dashboards de malha, `'16px 18px'`
//     no portal e `'24px 20px 24px 24px'` em espaço de trabalho. Era `sx` repetido
//     em cada card.
//  3. `borderLeft` + `selected` (L06) — o filete de 3px à esquerda e o estado
//     selecionado do `KpiSeletor` local de conta fiscal.
//  4. `iconTile` aceita objeto (L06, L19) — `size` 40|48, `shape` caixa|círculo e
//     `fill` gradiente|chapado|suave. `iconTile` booleano continua valendo e
//     significa `{ size: 40, shape: 'box', fill: 'gradient' }`, o desenho de hoje.
//  5. `tone='neutral'` (L05 K3) — o tom cinza `text.secondary` que faltava.
//  6. `titleLines` (L01 F3) — rótulo de duas linhas para "Receita Perdida p/ Outros
//     Municípios", que o `nowrap + ellipsis` do artboard corta.
//
// As três variantes que os lotes pediram por nome (`order='title-first'`,
// `scale='sans28'` e `border-left`) ficam consolidadas em props independentes:
// `order` e `scale` já existiam com esses valores exatos; `border-left` virou
// `borderLeft`. Não há prop-guarda-chuva de "variante": o canvas combina os três
// eixos livremente (o portal é `sans28` + `title-first` SEM filete; conta fiscal é
// `sans30` + `total-first` COM filete).

import type { Theme, SxProps } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';

import { varAlpha } from 'minimal-shared/utils';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Skeleton from '@mui/material/Skeleton';

import { DsIcon } from './DsIcon';
import { VOCABULARIO_KPI } from './vocabulario-kpi';

import type { DsIconName } from './DsIcon';
import type { ConceitoKpi } from './vocabulario-kpi';

// ----------------------------------------------------------------------

export type DsKpiTone =
  | 'primary'
  | 'secondary'
  | 'info'
  | 'success'
  | 'warning'
  | 'error'
  /** Cinza do canvas: ícone em `text.secondary`, acento em `grey`. */
  | 'neutral';

export type DsKpiScale = 'barlow32' | 'sans30' | 'sans28' | 'sans24';

/** Escala de padding do card, medida artboard a artboard em §5.2. */
export type DsKpiDensity = 'default' | 'dense' | 'compact' | 'wide';

export type DsKpiIconTile = {
  /** 40 é o tile do dashboard; 48 é o de conta fiscal. Padrão: 40. */
  size?: 40 | 48;
  /** `box` = raio 12 (canvas); `circle` = o círculo de `nota-nfse`. Padrão: `box`. */
  shape?: 'box' | 'circle';
  /**
   * `gradient` (padrão) = `linear-gradient(135deg, main, dark)` com ícone branco;
   * `flat` = cor chapada do tom com ícone branco (o círculo de `nota-nfse`);
   * `soft` = fundo em 12% do tom com o ícone em `dark` (o tile de conta fiscal).
   */
  fill?: 'gradient' | 'flat' | 'soft';
};

export type DsKpiCardProps = {
  total: React.ReactNode;
  title: string;
  sub?: React.ReactNode;
  /**
   * O que este número CONTA, no vocabulário do kit (`vocabulario-kpi.ts`).
   * Resolve `icon` e `tone` de uma vez: duas telas que contam a mesma coisa leem a
   * mesma linha do mapa e não têm como divergir de novo. É por aqui que os 96
   * cards se pintam — `icon`/`tone` na mão é a exceção, não a regra.
   */
  conceito?: ConceitoKpi;
  /** Ícone explícito. Vence o `conceito`. Sem nenhum dos dois, o card não tem ícone. */
  icon?: DsIconName;
  /** Tom explícito. Vence o `conceito`. Sem nenhum dos dois, `'primary'`. */
  tone?: DsKpiTone;
  scale?: DsKpiScale;
  order?: 'total-first' | 'title-first';
  /**
   * Escala de padding. Padrão: `'wide'` = `'24px 20px 24px 24px'`, o padding do
   * modelo aprovado pelo dono do produto. `'default'`, `'dense'` e `'compact'`
   * continuam valendo para quem precisar do valor que a §5.2 mede por família.
   */
  density?: DsKpiDensity;
  /** Filete de 3px à esquerda, na cor do tom (conta fiscal). */
  borderLeft?: boolean;
  /** Estado selecionado: borda no tom + fundo em 8% (o KPI que também é seletor). */
  selected?: boolean;
  /** 1 (padrão) = `nowrap` + ellipsis do artboard; 2 = rótulo longo em duas linhas. */
  titleLines?: 1 | 2;
  /** Esqueleto na geometria exata do card. Suspende o `onClick`. */
  loading?: boolean;
  /** Blob decorativo do dashboard. Padrão: ligado. `false` desliga. */
  ornament?: boolean;
  /**
   * Tile de ícone. `true` = tile 40×40 com gradiente (desenho do canvas).
   * Padrão: ligado — mas **só vale quando há `icon`**: sem ícone não existe tile,
   * porque um quadrado de 40×40 vazio é pior do que não ter tile nenhum.
   * `false` desliga e devolve o ícone solto de 44px.
   */
  iconTile?: boolean | DsKpiIconTile;
  onClick?: () => void;
  sx?: SxProps<Theme>;
};

// ----------------------------------------------------------------------

/**
 * As quatro definições de `.kpi-total` medidas no canvas (§5.2). O canvas não é
 * consistente aqui: a escala se escolhe pela família do artboard, nunca por média.
 * - `barlow32` (canônica): dashboard, dte-dashboard, os 6 dashboards malha-*,
 *   analise-fiscal, indexadores, imovel-avaliacoes, secao, ordem-servico-visao-geral,
 *   processo-dashboard.
 * - `sans30`: acao-fiscal*, arrecadacao*, atendimento/conta-fiscal, dívida,
 *   dte-mensagens, malha-execucao*, nota-nfse, processo-detalhe.
 * - `sans28`: todos os `portal-*` e ordem-servico.
 * - `sans24`: malha-dashboard.
 */
const ESCALAS: Record<DsKpiScale, { fontSize: number; fontWeight: number }> = {
  barlow32: { fontSize: 32, fontWeight: 700 },
  sans30: { fontSize: 30, fontWeight: 800 },
  sans28: { fontSize: 28, fontWeight: 800 },
  sans24: { fontSize: 24, fontWeight: 800 },
};

/**
 * Padding do card. A §5.2 mede um valor por família de artboard — `'18px'` nos 9
 * dashboards de malha, `'20px 22px'` em dte e ordem-servico, `'16px 18px'` no
 * portal, `'24px 20px 24px 24px'` em espaço de trabalho — e o mapa preserva os
 * quatro. O que mudou é qual deles é o default: `'wide'`, o do espaço de trabalho.
 *
 * A chave `'default'` mantém o nome por compatibilidade com quem já a passa; ela
 * é o antigo default (`'20px 22px'`), não o atual.
 */
const PADDINGS: Record<DsKpiDensity, string> = {
  default: '20px 22px',
  dense: '18px',
  compact: '16px 18px',
  wide: '24px 20px 24px 24px',
};

const TILE_PADRAO: Required<DsKpiIconTile> = { size: 40, shape: 'box', fill: 'gradient' };

// ----------------------------------------------------------------------

/**
 * Par `main`/`dark` do tom. `neutral` não é uma cor da paleta do MUI, então cai no
 * cinza — é o que o canvas desenha no KPI sem acento.
 */
function paletaDoTom(theme: Theme, tone: DsKpiTone): { main: string; dark: string } {
  if (tone === 'neutral') {
    return { main: theme.vars.palette.grey[600], dark: theme.vars.palette.grey[800] };
  }

  return { main: theme.vars.palette[tone].main, dark: theme.vars.palette[tone].dark };
}

/** Canal do tom, para as transparências (`varAlpha`). */
function canalDoTom(theme: Theme, tone: DsKpiTone): string {
  return tone === 'neutral'
    ? theme.vars.palette.grey['500Channel']
    : theme.vars.palette[tone].mainChannel;
}

/** Token de cor do ícone lateral (sem tile). */
function corDoIcone(tone: DsKpiTone): string {
  return tone === 'neutral' ? 'text.secondary' : `${tone}.main`;
}

// ----------------------------------------------------------------------

export function DsKpiCard({
  total,
  title,
  sub,
  conceito,
  icon: iconeExplicito,
  tone: tomExplicito,
  scale = 'barlow32',
  order = 'total-first',
  density = 'wide',
  borderLeft = false,
  selected = false,
  titleLines = 1,
  loading = false,
  ornament = true,
  iconTile = true,
  onClick,
  sx,
}: DsKpiCardProps) {
  /*
    Resolução do vocabulário, ADITIVA: o conceito só preenche o que a chamada não
    disse. `icon`/`tone` explícitos continuam vencendo — é assim que o card cujo
    tom depende do dado (uma taxa que só é `success` acima da meta) sobrevive sem
    sair do mapa.
  */
  const doVocabulario = conceito ? VOCABULARIO_KPI[conceito] : undefined;
  const icon = iconeExplicito ?? doVocabulario?.icon;
  const tone: DsKpiTone = tomExplicito ?? doVocabulario?.tone ?? 'primary';

  const escala = ESCALAS[scale];
  const primeiroTitulo = order === 'title-first';
  /**
   * O tile depende do ícone, não só da prop. Com `iconTile` ligado por default,
   * os 11 arquivos que nunca passaram `icon` renderizariam um quadrado 40×40 vazio
   * — pior do que o card que tinham antes. Sem `icon` não há tile, por mais que o
   * default diga o contrário: o default vale para quem TEM ícone.
   */
  const tile = icon && iconTile ? { ...TILE_PADRAO, ...(iconTile === true ? {} : iconTile) } : null;
  const temRealce = borderLeft || selected;

  const elTotal = (
    <Box
      key="total"
      component="p"
      sx={(theme) => ({
        ...(scale === 'barlow32' && { fontFamily: theme.typography.fontSecondaryFamily }),
        fontSize: escala.fontSize,
        fontWeight: escala.fontWeight,
        letterSpacing: '-0.02em',
        fontVariantNumeric: 'tabular-nums',
        m: 0,
      })}
    >
      {/*
        O esqueleto mora dentro do mesmo `<p>`: o `variant="text"` do MUI herda o
        `font-size` do contexto, então a linha do número carregando tem a MESMA
        altura da linha do número pronto. É isso que impede a faixa de KPIs de
        pular quando a resposta chega (§3.7, L03/L04/L05).
      */}
      {loading ? <Skeleton variant="text" width={96} /> : total}
    </Box>
  );

  const elTitulo = (
    <Box
      key="titulo"
      component="p"
      sx={{
        fontSize: 14,
        fontWeight: 600,
        color: 'text.secondary',
        // Espelha o 6px do canvas para o lado certo quando o rótulo vem primeiro.
        m: primeiroTitulo ? '0 0 6px' : '6px 0 0',
        ...(titleLines === 2
          ? {
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 2,
              overflow: 'hidden',
            }
          : { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
      }}
    >
      {loading ? <Skeleton variant="text" width={140} /> : title}
    </Box>
  );

  // Card clicável sem o hover `translateY(-2px)` + `boxShadow: 4` que as views de
  // dashboard adicionam hoje — proibido por §5.2. Carregando, o card não é botão:
  // não há o que abrir ainda.
  const propsClique =
    onClick && !loading
      ? {
          onClick,
          role: 'button',
          tabIndex: 0,
          onKeyDown: (evento: React.KeyboardEvent<HTMLDivElement>) => {
            if (evento.key === 'Enter' || evento.key === ' ') {
              evento.preventDefault();
              onClick();
            }
          },
        }
      : {};

  /**
   * `.kpi` / `.kpi.on` de conta fiscal: `border:1px solid transparent` com a
   * esquerda de 3px no acento. A borda transparente é o que mantém a caixa do mesmo
   * tamanho entre selecionado e não selecionado.
   */
  const sxRealce = (theme: Theme): SystemStyleObject<Theme> => {
    const cor = paletaDoTom(theme, tone);

    return {
      border: '1px solid',
      borderColor: selected ? cor.main : 'transparent',
      ...(borderLeft && { borderLeftWidth: '3px', borderLeftColor: cor.main }),
      ...(selected && { backgroundColor: varAlpha(canalDoTom(theme, tone), 0.08) }),
      ...(onClick && !loading && { '&:hover': { borderColor: cor.main } }),
    };
  };

  return (
    <Card
      {...propsClique}
      aria-busy={loading || undefined}
      sx={[
        {
          padding: PADDINGS[density],
          ...(onClick && !loading && { cursor: 'pointer' }),
          ...(ornament && { position: 'relative', overflow: 'hidden' }),
        },
        temRealce && sxRealce,
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {ornament && (
        <Box
          aria-hidden
          sx={(theme) => {
            const cor = paletaDoTom(theme, tone);

            return {
              position: 'absolute',
              top: -46,
              right: -104,
              width: 160,
              height: 160,
              borderRadius: '24px',
              transform: 'rotate(40deg)',
              opacity: 0.12,
              background: `linear-gradient(to right, ${cor.main}, ${cor.dark}, transparent)`,
            };
          }}
        />
      )}

      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '12px',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          {primeiroTitulo ? [elTitulo, elTotal] : [elTotal, elTitulo]}

          {sub && (
            <Box
              component="span"
              sx={{ fontSize: 11, color: 'text.secondary', display: 'block', mt: '4px' }}
            >
              {loading ? <Skeleton variant="text" width={90} /> : sub}
            </Box>
          )}
        </Box>

        {icon &&
          (tile ? (
            <Box
              sx={(theme) => {
                const cor = paletaDoTom(theme, tone);

                return {
                  flex: `0 0 ${tile.size}px`,
                  width: tile.size,
                  height: tile.size,
                  borderRadius: tile.shape === 'circle' ? '50%' : '12px',
                  display: 'grid',
                  placeItems: 'center',
                  ...(tile.fill === 'soft'
                    ? {
                        color: cor.dark,
                        backgroundColor: varAlpha(canalDoTom(theme, tone), 0.12),
                      }
                    : {
                        color: 'common.white',
                        background:
                          tile.fill === 'flat'
                            ? cor.main
                            : `linear-gradient(135deg, ${cor.main}, ${cor.dark})`,
                      }),
                };
              }}
            >
              {/*
                22px é o que §5.2 mede dentro do tile, e `DsIconSize` é uma união
                fechada — o tile de 48px de conta fiscal fica com o mesmo glifo de
                22px. Se o canvas exigir 26px ali, quem abre a união é `DsIcon`.
              */}
              {loading ? (
                <Skeleton
                  variant={tile.shape === 'circle' ? 'circular' : 'rounded'}
                  width={tile.size}
                  height={tile.size}
                />
              ) : (
                <DsIcon name={icon} size={22} />
              )}
            </Box>
          ) : (
            <Box sx={{ flex: '0 0 auto', color: corDoIcone(tone), display: 'grid' }}>
              {loading ? (
                <Skeleton variant="circular" width={44} height={44} />
              ) : (
                <DsIcon name={icon} size={44} sx={{ '& svg': { strokeWidth: 1.4 } }} />
              )}
            </Box>
          ))}
      </Box>
    </Card>
  );
}
